import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconDownload,
  IconLock,
  IconUser,
  IconClock,
  IconTractor,
  IconCamera,
} from '@tabler/icons-react';

export default function CheckDetail() {
  const { t, language } = useTranslation();
  const { checkId } = useParams();
  const navigate = useNavigate();
  const { orgId } = useAuth();

  const [check, setCheck] = useState(null);
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !checkId) return;
      try {
        // Search across all machines for this check record
        const machinesSnap = await getDocs(
          collection(db, 'organisations', orgId, 'machines')
        );

        let foundCheck = null;
        let foundMachine = null;

        for (const machineDoc of machinesSnap.docs) {
          const checkSnap = await getDoc(
            doc(db, 'organisations', orgId, 'machines', machineDoc.id, 'checkRecords', checkId)
          );
          if (checkSnap.exists()) {
            foundCheck = { id: checkSnap.id, ...checkSnap.data() };
            foundMachine = { id: machineDoc.id, ...machineDoc.data() };
            break;
          }
        }

        setCheck(foundCheck);
        setMachine(foundMachine);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, checkId]);

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const passedItems = check?.sections?.flatMap(s => s.items || []).filter(i => i.result === 'pass') || [];
  const failedItems = check?.sections?.flatMap(s => s.items || []).filter(i => i.result === 'fail') || [];
  const totalItems = passedItems.length + failedItems.length;

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
        {t('common.loading')}
      </div>
    </Layout>
  );

  if (!check) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div
          onClick={() => navigate('/checks')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}
        >
          <IconArrowLeft size={15} stroke={1.5} /> {t('checks.history_title')}
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>{t('checks.not_found')}</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        {/* Back */}
        <div
          onClick={() => navigate('/checks')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}
        >
          <IconArrowLeft size={15} stroke={1.5} /> {t('checks.history_title')}
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 22, fontWeight: 400 }}>
                {check.templateName || t('daily_check.title')}
              </h1>
              <StatusPill status={check.result || 'pending'} size="lg" />
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {machine?.makeLabel} {machine?.modelLabel}
              {machine?.registration && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, background: '#FAC775', color: '#412402', padding: '1px 6px', borderRadius: 3, marginLeft: 8 }}>
                  {machine.registration}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={<IconDownload size={13} stroke={1.5} />}
            onClick={() => toast.info(t('common.pdf_coming_soon'))}
          >
            {t('common.export_pdf')}
          </Button>
        </div>

        {/* Failed banner */}
        {check.result === 'fail' && (
          <div style={{
            background: 'var(--color-danger-light)',
            border: '0.5px solid var(--color-danger)',
            borderRadius: 'var(--radius-lg)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 20,
          }}>
            <IconAlertTriangle size={18} color="var(--color-danger)" stroke={1.5} />
            <div>
              <div style={{ fontSize: 13, color: 'var(--color-danger)', fontWeight: 400 }}>
                {failedItems.length} {t(failedItems.length === 1 ? 'checks.item_failed_one' : 'checks.item_failed_many')}
              </div>
              {check.machineBlocked && (
                <div style={{ fontSize: 12, color: 'var(--color-danger)', opacity: 0.8, marginTop: 2 }}>
                  {t('checks.auto_blocked')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sealed banner */}
        <div style={{
          background: '#E6F1FB',
          border: '0.5px solid #185FA5',
          borderRadius: 'var(--radius-lg)',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 20,
        }}>
          <IconLock size={14} color="#185FA5" stroke={1.5} />
          <div style={{ fontSize: 12, color: '#185FA5' }}>
            {t('checks.sealed_record')} · {t('checks.submitted_by')} {check.submittedByName} · {formatDateTime(check.submittedAt)}
          </div>
        </div>

        {/* Key stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: t('checks.stat_total_items'), value: totalItems, colour: 'var(--color-text-primary)' },
            { label: t('checks.stat_passed'), value: passedItems.length, colour: 'var(--color-success)' },
            { label: t('checks.stat_failed'), value: failedItems.length, colour: failedItems.length > 0 ? 'var(--color-danger)' : 'var(--color-success)' },
            { label: t('checks.stat_mileage'), value: check.mileage ? `${check.mileage.toLocaleString()} ${t('common.unit_mi')}` : '—', colour: 'var(--color-text-primary)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '12px 14px' }}>
              <div style={{ fontSize: 22, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Operator & machine info */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('checks.details_title')}</div>
          {[
            { icon: IconUser,    label: t('checks.submitted_by'),    value: check.submittedByName || '—'                    },
            { icon: IconClock,   label: t('checks.submitted_at'),    value: formatDateTime(check.submittedAt)               },
            { icon: IconTractor, label: t('machines.col_machine'),   value: `${machine?.makeLabel} ${machine?.modelLabel}` },
            { icon: IconClock,   label: t('checks.hours_at_check'),  value: check.hoursAtCheck ? `${check.hoursAtCheck.toLocaleString()} ${t('common.unit_hrs')}` : '—' },
          ].map(row => {
            const Icon = row.icon;
            return (
              <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <Icon size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 120, flexShrink: 0 }}>{row.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
              </div>
            );
          })}
        </Card>

        {/* Failed items first */}
        {failedItems.length > 0 && (
          <Card style={{ marginBottom: 16, border: '0.5px solid var(--color-danger)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />
              <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-danger)' }}>
                {t('checks.failed_items')} ({failedItems.length})
              </div>
            </div>
            {failedItems.map((item, i) => (
              <div
                key={i}
                style={{
                  padding: '12px 0',
                  borderBottom: i < failedItems.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: item.note ? 8 : 0 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 5, background: 'var(--color-danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <IconX size={13} color="#fff" stroke={2} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--color-danger)', fontWeight: 400 }}>{item.label}</div>
                    {item.sectionLabel && (
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{item.sectionLabel}</div>
                    )}
                  </div>
                </div>
                {item.note && (
                  <div style={{ marginLeft: 32, background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 12, color: 'var(--color-danger)', lineHeight: 1.5 }}>
                    {item.note}
                  </div>
                )}
                {item.photoURL && (
                  <div style={{ marginLeft: 32, marginTop: 8 }}>
                    <img
                      src={item.photoURL}
                      alt={t('checks.alt_defect_photo')}
                      style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-light)' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        {/* All sections */}
        {check.sections?.map((section, si) => (
          <Card key={si} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', letterSpacing: '0.06em', marginBottom: 12 }}>
              {section.label?.toUpperCase()}
            </div>
            {section.items?.map((item, ii) => (
              <div
                key={ii}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '9px 0',
                  borderBottom: ii < section.items.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                }}
              >
                <div style={{
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  background: item.result === 'pass' ? 'var(--color-success)' : item.result === 'fail' ? 'var(--color-danger)' : 'var(--color-bg-tertiary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: 1,
                }}>
                  {item.result === 'pass' && <IconCheck size={13} color="#fff" stroke={2} />}
                  {item.result === 'fail' && <IconX size={13} color="#fff" stroke={2} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: item.result === 'fail' ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
                    {item.label}
                  </div>
                  {item.note && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4, lineHeight: 1.5 }}>
                      {item.note}
                    </div>
                  )}
                  {item.photoURL && (
                    <div style={{ marginTop: 6 }}>
                      <img
                        src={item.photoURL}
                        alt={t('checks.alt_check_photo')}
                        style={{ width: 100, height: 68, objectFit: 'cover', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-light)' }}
                      />
                    </div>
                  )}
                </div>
                <StatusPill status={item.result || 'pending'} />
              </div>
            ))}
          </Card>
        ))}

        {/* No sections fallback */}
        {(!check.sections || check.sections.length === 0) && (
          <Card>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', textAlign: 'center', padding: '20px 0' }}>
              {t('checks.no_items_recorded')}
            </div>
          </Card>
        )}

      </div>
    </Layout>
  );
}